import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/user.types';
import toast from 'react-hot-toast';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.RINGER);
  const [auctionHouseName, setAuctionHouseName] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (role === UserRole.AUCTIONEER && !auctionHouseName) {
      toast.error('Please enter your auction house name');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      console.log('Starting registration...', { email, role, auctionHouseName });
      await register(email, password, role, auctionHouseName);
      console.log('Registration successful!');
      toast.success('Account created successfully!');
      
      // Navigate based on role
      const destination = role === UserRole.AUCTIONEER ? '/auctioneer/dashboard' : '/dashboard';
      console.log('Navigating to:', destination);
      navigate(destination);
    } catch (error: any) {
      console.error('Registration error:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      toast.error(error.message || 'Failed to create account');
      setLoading(false);
    }
  };

  return (
    <div className="card max-w-md mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-center">Register</h1>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Role Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            I want to:
          </label>
          <div className="space-y-2">
            <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                value={UserRole.RINGER}
                checked={role === UserRole.RINGER}
                onChange={(e) => setRole(e.target.value as UserRole)}
                className="mr-3"
              />
              <div>
                <div className="font-semibold">Predict Auctions (Ringer)</div>
                <div className="text-sm text-gray-600">Make predictions and earn Ringer$</div>
              </div>
            </label>
            
            <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                value={UserRole.AUCTIONEER}
                checked={role === UserRole.AUCTIONEER}
                onChange={(e) => setRole(e.target.value as UserRole)}
                className="mr-3"
              />
              <div>
                <div className="font-semibold">Host Auctions (Auctioneer)</div>
                <div className="text-sm text-gray-600">Create and manage auctions</div>
              </div>
            </label>
          </div>
        </div>

        {/* Auction House Name (only for auctioneers) */}
        {role === UserRole.AUCTIONEER && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auction House Name
            </label>
            <input
              type="text"
              value={auctionHouseName}
              onChange={(e) => setAuctionHouseName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Your Auction House"
              disabled={loading}
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="your@email.com"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="••••••••"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="••••••••"
            disabled={loading}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating account...' : 'Register'}
        </button>
      </form>

      <p className="mt-4 text-center text-gray-600">
        Already have an account?{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold">
          Login here
        </Link>
      </p>
    </div>
  );
};

export default Register;